/**
 * Servicio de afiliación médica (Fase 1).
 *
 * Cubre:
 *  - `submitAffiliationRequest` (anon, vía RPC pública).
 *  - RPCs admin para listar/triage/rechazar/aprobar (sin crear doctor).
 *  - `adminCountAffiliationPending` para el badge en sidebar admin.
 *
 * Diseño defensivo (igual que claimProfile.service.ts):
 *   - Mantenemos uso de `supabase.rpc` para los wrappers admin
 *     (sesiones autenticadas, sin acumulación crítica).
 *   - Para el submit público usamos fetch directo a PostgREST: evita
 *     hangs del wrapper supabase-js si el navegador tiene
 *     `navigator.locks` tomado por otra cosa, y permite controlar
 *     timeout explícito.
 *
 * No expone tipos del DB directamente; mapea a interfaces TS limpias.
 */

import { supabase } from '@/lib/supabase'

export type AffiliationStatus = 'pending' | 'in_review' | 'approved' | 'rejected' | 'expired'

export interface AffiliationRequestRow {
  id: string
  createdAt: string
  updatedAt: string
  fullName: string
  phone: string
  phoneNormalized: string
  email: string | null
  specialtyId: string | null
  specialtyName: string | null
  specialtyOther: string | null
  licenseNumber: string | null
  departmentId: string | null
  departmentName: string | null
  municipalityId: string | null
  municipalityName: string | null
  addressLine: string | null
  clinicName: string | null
  message: string | null
  status: AffiliationStatus
  incomplete: boolean
  adminNotes: string | null
  reviewedAt: string | null
  reviewedByName: string | null
  doctorId: string | null
  clinicId: string | null
}

export interface AffiliationRequestsPage {
  rows: AffiliationRequestRow[]
  total: number
}

export interface AffiliationFilters {
  status?: AffiliationStatus | null
  incomplete?: boolean | null
  search?: string | null
  limit?: number
  offset?: number
}

export interface SubmitAffiliationPayload {
  fullName: string
  phone: string
  consentVersion: string
  email?: string | null
  specialtyId?: string | null
  specialtyOther?: string | null
  licenseNumber?: string | null
  departmentId?: string | null
  municipalityId?: string | null
  addressLine?: string | null
  clinicName?: string | null
  message?: string | null
}

// ─── Submit público (fetch directo, mismo patrón que claimProfile) ───

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const SUBMIT_TIMEOUT_MS = 8_000

export type SubmitAffiliationErrorCode =
  | 'INVALID_NAME' // P0001
  | 'INVALID_PHONE' // P0002
  | 'INVALID_CONSENT' // P0003
  | 'RATE_LIMITED' // P0010
  | 'UNKNOWN'

const SQLSTATE_TO_CODE: Record<string, SubmitAffiliationErrorCode> = {
  P0001: 'INVALID_NAME',
  P0002: 'INVALID_PHONE',
  P0003: 'INVALID_CONSENT',
  P0010: 'RATE_LIMITED',
}

export interface SubmitAffiliationResult {
  success: boolean
  errorCode?: SubmitAffiliationErrorCode
  errorMessage?: string
}

export async function submitAffiliationRequest(
  payload: SubmitAffiliationPayload,
): Promise<SubmitAffiliationResult> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS)

  try {
    const resp = await fetch(`${SUPABASE_URL}/rest/v1/rpc/submit_affiliation_request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Accept: 'application/json',
        // sin Authorization → corre como anon
      },
      body: JSON.stringify({
        p_full_name: payload.fullName,
        p_phone: payload.phone,
        p_consent_version: payload.consentVersion,
        p_email: payload.email ?? null,
        p_specialty_id: payload.specialtyId ?? null,
        p_specialty_other: payload.specialtyOther ?? null,
        p_license_number: payload.licenseNumber ?? null,
        p_department_id: payload.departmentId ?? null,
        p_municipality_id: payload.municipalityId ?? null,
        p_address_line: payload.addressLine ?? null,
        p_clinic_name: payload.clinicName ?? null,
        p_message: payload.message ?? null,
      }),
      signal: controller.signal,
    })
    clearTimeout(timeoutId)

    const text = await resp.text()
    let body: unknown = null
    try { body = text ? JSON.parse(text) : null } catch { body = text }

    if (!resp.ok) {
      const errBody = (body || {}) as { code?: string; message?: string }
      const code = errBody.code ? SQLSTATE_TO_CODE[errBody.code] : undefined
      return {
        success: false,
        errorCode: code ?? 'UNKNOWN',
        errorMessage: errBody.message ?? `HTTP ${resp.status}`,
      }
    }

    const ok = !!(body && typeof body === 'object' && (body as { success?: boolean }).success)
    return ok ? { success: true } : { success: false, errorCode: 'UNKNOWN', errorMessage: 'Respuesta inesperada' }
  } catch (err) {
    clearTimeout(timeoutId)
    const isAbort = err instanceof DOMException && err.name === 'AbortError'
    return {
      success: false,
      errorCode: 'UNKNOWN',
      errorMessage: isAbort
        ? 'La solicitud tardó más de lo normal. Probá de nuevo.'
        : err instanceof Error ? err.message : 'Error de red',
    }
  }
}

// ─── Wrappers admin (usan supabase.rpc — sesión activa, sin riesgo de hang) ───

function mapRow(r: Record<string, unknown>): AffiliationRequestRow {
  return {
    id: r.id as string,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
    fullName: r.full_name as string,
    phone: r.phone as string,
    phoneNormalized: r.phone_normalized as string,
    email: (r.email as string) ?? null,
    specialtyId: (r.specialty_id as string) ?? null,
    specialtyName: (r.specialty_name as string) ?? null,
    specialtyOther: (r.specialty_other as string) ?? null,
    licenseNumber: (r.license_number as string) ?? null,
    departmentId: (r.department_id as string) ?? null,
    departmentName: (r.department_name as string) ?? null,
    municipalityId: (r.municipality_id as string) ?? null,
    municipalityName: (r.municipality_name as string) ?? null,
    addressLine: (r.address_line as string) ?? null,
    clinicName: (r.clinic_name as string) ?? null,
    message: (r.message as string) ?? null,
    status: r.status as AffiliationStatus,
    incomplete: !!r.incomplete,
    adminNotes: (r.admin_notes as string) ?? null,
    reviewedAt: (r.reviewed_at as string) ?? null,
    reviewedByName: (r.reviewed_by_name as string) ?? null,
    doctorId: (r.doctor_id as string) ?? null,
    clinicId: (r.clinic_id as string) ?? null,
  }
}

export async function adminListAffiliationRequests(
  filters: AffiliationFilters = {},
): Promise<AffiliationRequestsPage> {
  const { data, error } = await supabase.rpc('admin_list_affiliation_requests', {
    p_status: filters.status ?? null,
    p_incomplete: filters.incomplete ?? null,
    p_search: filters.search?.trim() || null,
    p_limit: filters.limit ?? 25,
    p_offset: filters.offset ?? 0,
  })
  if (error) throw new Error(error.message)
  const arr = (data ?? []) as Array<Record<string, unknown>>
  const rows = arr.map(mapRow)
  const total = arr.length > 0 ? Number(arr[0].total_count ?? 0) : 0
  return { rows, total }
}

export async function adminMarkInReview(id: string, notes?: string | null): Promise<void> {
  const { error } = await supabase.rpc('admin_mark_in_review', { p_id: id, p_notes: notes ?? null })
  if (error) throw new Error(error.message)
}

export async function adminRejectAffiliationRequest(id: string, notes: string): Promise<void> {
  const { error } = await supabase.rpc('admin_reject_affiliation_request', { p_id: id, p_notes: notes })
  if (error) throw new Error(error.message)
}

export async function adminMarkApprovedPendingCreation(id: string, notes?: string | null): Promise<void> {
  const { error } = await supabase.rpc('admin_mark_approved_pending_creation', {
    p_id: id,
    p_notes: notes ?? null,
  })
  if (error) throw new Error(error.message)
}

export async function adminCountAffiliationPending(): Promise<number> {
  const { data, error } = await supabase.rpc('admin_count_affiliation_pending')
  if (error) return 0
  return Number(data ?? 0)
}

// ─── Fase 2: aprobar y crear médico ───

/**
 * Overrides aceptados por `admin_approve_and_create_doctor`.
 * Phone NUNCA se sobreescribe (identidad del lead, ya validada via OTP
 * cuando el médico reclame). Email solo se acepta como override si el
 * lead NO trajo email (regla server-side en s7_23) — si el lead sí
 * tenía email, el RPC lo ignora silenciosamente.
 */
export interface ApproveAndCreateOverrides {
  fullName?: string | null
  email?: string | null
  specialtyId?: string | null
  clinicName?: string | null
  addressLine?: string | null
  departmentId?: string | null
  municipalityId?: string | null
  /** Confirmación explícita para reusar la cuenta de un paciente existente. */
  confirmReuse?: boolean
}

export interface ApproveAndCreateResult {
  doctorId: string
  clinicId: string
  profileId: string
  /** True si el RPC aceptó el email del override (porque lead no tenía). */
  emailViaOverride?: boolean
  /** True si se reusó la cuenta de un paciente existente (no se creó auth.user). */
  reusedExistingUser?: boolean
}

// ─── Preflight de afiliación (s7_42) ───────────────────────────────────

export type AffiliationClassification =
  | 'new'
  | 'reuse_patient'
  | 'block_doctor'
  | 'block_sensitive'
  | 'block_identity_conflict'

export interface AffiliationPreflight {
  classification: AffiliationClassification
  existingUserId: string | null
  existingDoctorId: string | null
  leadStatus: AffiliationStatus
  alreadyLinked: boolean
}

/**
 * Clasifica el lead ANTES de crear el médico (RPC `admin_affiliation_preflight`,
 * s7_42, definer, `is_admin()`). No escribe. La UI lo usa para mostrar el
 * copy/confirmación correctos (paciente existente → vincular; médico existente
 * → bloquear con link; rol sensible / identidad ambigua → revisión manual).
 */
export async function adminAffiliationPreflight(
  requestId: string,
): Promise<AffiliationPreflight> {
  const { data, error } = await supabase.rpc('admin_affiliation_preflight', {
    p_request_id: requestId,
  })
  if (error) throw new Error(error.message)
  const r = data as {
    classification: AffiliationClassification
    existing_user_id: string | null
    existing_doctor_id: string | null
    lead_status: AffiliationStatus
    already_linked: boolean
  }
  return {
    classification: r.classification,
    existingUserId: r.existing_user_id,
    existingDoctorId: r.existing_doctor_id,
    leadStatus: r.lead_status,
    alreadyLinked: r.already_linked,
  }
}

/** Código de error amigable de la creación de médico desde afiliación. */
export type AffiliationCreateErrorCode =
  | 'block_doctor'             // P0010
  | 'block_sensitive'          // P0011
  | 'block_identity_conflict'  // P0012
  | 'requires_confirmation'    // P0013
  | 'invalid_email'            // P0005
  | 'not_approved'             // P0002
  | 'already_linked'           // P0003
  | 'jvpm_duplicate'           // P0091 (colisión doctor_credentials_registry_uniq — s7_62)
  | 'specialty_inactive'       // P0195 (s7_99, D2: la especialidad elegida está inactiva)
  | 'duplicate'                // 23505 (fallback — no debería ocurrir con s7_42)
  | 'unknown'

export class AffiliationCreateError extends Error {
  readonly code: AffiliationCreateErrorCode
  constructor(code: AffiliationCreateErrorCode, message: string) {
    super(message)
    this.name = 'AffiliationCreateError'
    this.code = code
  }
}

const CREATE_ERROR_COPY: Record<AffiliationCreateErrorCode, string> = {
  block_doctor:
    'Ya existe un médico registrado con este teléfono. Revisá su ficha; podría ser un duplicado.',
  block_sensitive:
    'Este teléfono pertenece a una cuenta con otro rol (asistente/administrador) o con una clínica activa. Requiere revisión manual.',
  block_identity_conflict:
    'El correo del lead pertenece a otra cuenta o no coincide con el teléfono. Identidad ambigua: requiere revisión manual.',
  requires_confirmation:
    'Este teléfono ya pertenece a una cuenta de paciente. Confirmá la vinculación para crear el médico sobre esa cuenta.',
  invalid_email: 'El email indicado no tiene un formato válido.',
  not_approved: 'La solicitud no está en estado aprobado.',
  already_linked: 'Esta solicitud ya tiene un médico vinculado.',
  jvpm_duplicate:
    'Ese número de licencia (JVPM) ya pertenece a otro médico. Verificá el dato antes de crear la ficha.',
  specialty_inactive:
    'La especialidad elegida está inactiva y no se puede asignar. Elegí una especialidad activa o dejá sin asignar.',
  duplicate:
    'Ya existe una cuenta con esos datos (teléfono o email). Requiere revisión manual.',
  unknown: 'No pudimos crear el médico. Revisá los datos e intentá de nuevo.',
}

function mapCreateError(err: { code?: string; message?: string } | null): AffiliationCreateError {
  const sqlstate = err?.code ?? ''
  const byCode: Record<string, AffiliationCreateErrorCode> = {
    P0010: 'block_doctor',
    P0011: 'block_sensitive',
    P0012: 'block_identity_conflict',
    P0013: 'requires_confirmation',
    P0005: 'invalid_email',
    P0002: 'not_approved',
    P0003: 'already_linked',
    P0091: 'jvpm_duplicate',
    P0195: 'specialty_inactive',
    '23505': 'duplicate',
  }
  const code: AffiliationCreateErrorCode =
    byCode[sqlstate] ?? (/users_phone_key|users_email|duplicate key/i.test(err?.message ?? '') ? 'duplicate' : 'unknown')
  return new AffiliationCreateError(code, CREATE_ERROR_COPY[code])
}

export async function adminApproveAndCreateDoctor(
  requestId: string,
  overrides: ApproveAndCreateOverrides = {},
): Promise<ApproveAndCreateResult> {
  // Construir el payload de overrides en snake_case que espera el RPC.
  // Solo incluimos las claves no vacías para que el plpgsql aplique
  // COALESCE(override > lead).
  // Excepción (SPECIALTIES-ADMIN-P1): la especialidad va por PRESENCIA de clave.
  // Si el llamador la decide (también null = «Sin asignar»), se envía siempre y el
  // servidor NO hereda la del lead; si no la indica (undefined), se omite y la hereda.
  const payload: Record<string, string | null> = {}
  if (overrides.fullName && overrides.fullName.trim()) payload.full_name = overrides.fullName.trim()
  if (overrides.email && overrides.email.trim()) payload.email = overrides.email.trim()
  if (overrides.specialtyId !== undefined) payload.specialty_id = overrides.specialtyId || null
  if (overrides.clinicName && overrides.clinicName.trim()) payload.clinic_name = overrides.clinicName.trim()
  if (overrides.addressLine && overrides.addressLine.trim()) payload.address_line = overrides.addressLine.trim()
  if (overrides.departmentId) payload.department_id = overrides.departmentId
  if (overrides.municipalityId) payload.municipality_id = overrides.municipalityId
  if (overrides.confirmReuse) payload.confirm_reuse = 'true'

  const { data, error } = await supabase.rpc('admin_approve_and_create_doctor', {
    p_request_id: requestId,
    p_overrides: payload,
  })
  if (error) throw mapCreateError(error)
  const row = data as {
    doctor_id: string
    clinic_id: string
    profile_id: string
    email_via_override?: boolean
    reused_existing_user?: boolean
  }
  return {
    doctorId: row.doctor_id,
    clinicId: row.clinic_id,
    profileId: row.profile_id,
    emailViaOverride: !!row.email_via_override,
    reusedExistingUser: !!row.reused_existing_user,
  }
}

// ═══════════════════════════════════════════════════════════
// Correo de bienvenida al médico (DOCTOR-WELCOME-EMAIL-P0)
// ═══════════════════════════════════════════════════════════
// El envío NO es automático: lo dispara el owner desde LucyAdmin después de
// aprobar y publicar. Todos los gates son server-side; lo de acá es UX.

/** Motivo por el que la bienvenida puede o no enviarse. Clave estable de la RPC. */
export type WelcomeReason =
  | 'ok'
  | 'no_doctor'
  | 'no_email'
  | 'not_published'
  | 'no_slug'
  | 'already_claimed'
  | 'already_sent'
  | 'sending_recent'
  | 'needs_review'

export type WelcomeStatus = 'not_sent' | 'sending' | 'sent' | 'failed'

export interface WelcomeEmailState {
  status: WelcomeStatus
  sentAt: string | null
  lastAttemptAt: string | null
  canSend: boolean
  reason: WelcomeReason
}

export async function getWelcomeEmailState(requestId: string): Promise<WelcomeEmailState> {
  const { data, error } = await supabase.rpc('admin_welcome_email_state', {
    p_request_id: requestId,
  })
  if (error) throw new Error(error.message)
  const s = (data ?? {}) as Record<string, unknown>
  return {
    status: (s.status as WelcomeStatus) ?? 'not_sent',
    sentAt: (s.sent_at as string) ?? null,
    lastAttemptAt: (s.last_attempt_at as string) ?? null,
    canSend: !!s.can_send,
    reason: (s.reason as WelcomeReason) ?? 'needs_review',
  }
}

/**
 * Dispara el envío. La Edge Function recibe SOLO el id de la solicitud: el
 * correo, el nombre y el slug los resuelve la base, nunca el navegador.
 *
 * El `Authorization` va explícito a propósito, igual que en
 * `adminDoctorSeed.service.ts`: la función evalúa `is_admin()` con ESE token y
 * no se delega en que supabase-js lo adjunte solo.
 */
export async function sendWelcomeEmail(requestId: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession()
  const token = sessionData.session?.access_token
  if (!token) throw new Error('not_authenticated')

  const { error } = await supabase.functions.invoke('send-doctor-welcome-email', {
    body: { affiliation_request_id: requestId },
    headers: { Authorization: `Bearer ${token}` },
  })
  if (error) throw new Error('send_failed')
}
